/** @fileoverview Renders the live Fabrik message composer. */

import { useEffect, useRef, useState } from 'react';
import {
  ArrowUp,
  Check,
  ChevronUp,
  Paperclip,
  Layers,
  SquareDashedMousePointer,
  ListChecks,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import type { ClipboardEvent, DragEvent, KeyboardEvent } from 'react';
import { ACCEPT_ATTRIBUTE, MAX_ATTACHMENTS_PER_MESSAGE, omittedCount } from '../../lib/attachments';
import AttachmentChips from './AttachmentChips';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Textarea } from '../ui/textarea';
import type { ChatComposerModelToggleProps, ChatComposerProps } from './types';

function SelectionButton({
  canSelectPreview,
  selectionMode,
  selectionCount,
  title,
  onClear,
}: {
  canSelectPreview: boolean;
  selectionMode: boolean;
  selectionCount: number;
  title: string;
  onClear?: () => void;
}) {
  const stacked = selectionCount > 0;
  const label = stacked
    ? `${selectionCount} queued ${selectionCount === 1 ? 'selection' : 'selections'}`
    : selectionMode
    ? 'Disable selection tool'
    : 'Select elements in the preview';

  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-xs transition-colors disabled:cursor-default disabled:opacity-50 ${
          selectionMode || stacked
            ? 'border-app-brand-subtle bg-app-brand-subtle text-app-brand-subtle-foreground'
            : 'border-app-border text-app-text-muted hover:bg-app-surface-muted hover:text-app-text'
        }`}
        aria-label={label}
        aria-pressed={selectionMode}
        title={stacked ? `${title} — Ctrl+Enter to send` : label}
        disabled={!canSelectPreview && !selectionMode}
        onClick={() => window.dispatchEvent(new CustomEvent('fabrik:preview-selection-toggle'))}
      >
        {stacked ? (
          <Layers className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <SquareDashedMousePointer className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {stacked && selectionCount}
      </button>
      {stacked && (
        <button
          type="button"
          className="absolute -right-1.5 -top-1.5 hidden size-4 items-center justify-center rounded-full border border-app-border bg-app-surface-card text-app-text-muted shadow-sm hover:text-app-text focus-visible:flex group-hover:flex"
          aria-label="Clear selections"
          title="Clear selections"
          onClick={onClear}
        >
          <X className="h-2.5 w-2.5" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}

function ModelToggle({ model, disabled, onChange }: ChatComposerModelToggleProps) {
  const [open, setOpen] = useState(false);
  const fast = model === 'fast';

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const selectModel = (nextModel: string) => {
    onChange(nextModel);
    setOpen(false);
  };

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverTrigger
        className="flex h-7 items-center gap-1 rounded-full bg-app-brand-subtle px-2 text-sm text-app-brand-subtle-foreground hover:bg-app-brand-subtle disabled:cursor-default disabled:opacity-60"
        type="button"
        aria-label={`AI mode: ${fast ? 'Fast' : 'Intelligent'}`}
        disabled={disabled}
      >
        {fast ? (
          <Zap className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Sparkles className="h-4 w-4" aria-hidden="true" />
        )}
        <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-52 rounded-lg bg-app-surface-card p-1.5 shadow-md"
      >
        <Button
          variant="ghost"
          className="h-auto w-full justify-start gap-2 rounded-md px-2.5 py-2 text-left font-normal text-app-text aria-pressed:bg-app-surface-muted-strong hover:bg-app-brand-subtle!"
          aria-pressed={fast}
          type="button"
          onClick={() => selectModel('fast')}
        >
          <Zap className="h-4 w-4 text-app-brand-subtle-foreground" aria-hidden="true" />
          <span className="flex-1">
            <strong className="block text-xs font-semibold text-app-text">Fast</strong>
            <small className="block text-xs text-app-text-muted">Quick responses</small>
          </span>
          {fast && (
            <Check className="h-4 w-4 text-app-brand-subtle-foreground" aria-hidden="true" />
          )}
        </Button>
        <Button
          variant="ghost"
          className="h-auto w-full justify-start gap-2 rounded-md px-2.5 py-2 text-left font-normal text-app-text aria-pressed:bg-app-surface-muted-strong hover:bg-app-brand-subtle!"
          aria-pressed={!fast}
          type="button"
          onClick={() => selectModel('intelligent')}
        >
          <Sparkles className="h-4 w-4 text-app-brand-subtle-foreground" aria-hidden="true" />
          <span className="flex-1">
            <strong className="block text-xs font-semibold text-app-text">Intelligent</strong>
            <small className="block text-xs text-app-text-muted">Better reasoning</small>
          </span>
          {!fast && (
            <Check className="h-4 w-4 text-app-brand-subtle-foreground" aria-hidden="true" />
          )}
        </Button>
      </PopoverContent>
    </Popover>
  );
}

export default function ChatComposer({
  value,
  busy,
  loading,
  quotaExhausted,
  model,
  onModelChange,
  onChange,
  onSend,
  onStop,
  placeholder,
  welcome = false,
  showPlanModeToggle = false,
  planMode = false,
  onPlanModeChange,
  previewSelection = [],
  onClearPreviewSelection,
  previewSelectionToolAvailable = false,
  showSelectionButton = false,
  attachments = [],
  attachmentsBlocking = false,
  onAttach,
  onRemoveAttachment,
  onRetryAttachment,
  conversationFiles,
}: ChatComposerProps & {
  showPlanModeToggle?: boolean;
  planMode?: boolean;
  onPlanModeChange?: () => void;
}) {
  const [selectionMode, setSelectionMode] = useState(false);
  // Each drag or click in the preview is one numbered selection (group) with an optional comment.
  const selectionCount = new Set(previewSelection.map((item) => item.group ?? item.path)).size;
  // Queued selections can be sent on their own, without a typed message.
  const canSend = Boolean(value.trim()) || selectionCount > 0 || attachments.length > 0;
  const overBudget = omittedCount(conversationFiles);
  const fileInput = useRef<HTMLInputElement>(null);
  const selectionTitle = previewSelection.some((item) => item.note)
    ? previewSelection
        .filter((item) => item.note)
        .map((item) => `${item.group ?? ''}. ${item.note}`)
        .join(' · ')
    : `${selectionCount} ${selectionCount === 1 ? 'selection' : 'selections'}`;

  useEffect(() => {
    const onSelectionModeChanged = (event: Event) => {
      setSelectionMode((event as CustomEvent<boolean>).detail === true);
    };
    window.addEventListener('fabrik:preview-selection-mode-changed', onSelectionModeChanged);
    return () =>
      window.removeEventListener('fabrik:preview-selection-mode-changed', onSelectionModeChanged);
  }, []);

  const attach = (files: File[]) => {
    if (onAttach && files.length) onAttach(files);
  };
  const onPaste = (event: ClipboardEvent) => {
    const files = Array.from(event.clipboardData.files);
    // Copying from Excel or Word puts an image and text on the clipboard; keep the text paste.
    if (!files.length || !onAttach || event.clipboardData.getData('text/plain')) return;
    event.preventDefault();
    attach(files);
  };
  const onDrop = (event: DragEvent) => {
    if (!onAttach || !event.dataTransfer.types.includes('Files')) return;
    event.preventDefault();
    attach(Array.from(event.dataTransfer.files));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.currentTarget.form?.requestSubmit();
    }
  };

  return (
    <div className="mt-1 w-full min-w-0">
      <form
        className={`flex w-full min-w-0 flex-col rounded-xl border border-app-border-strong bg-app-surface-card transition-colors hover:border-app-brand-strong focus-within:border-app-brand-strong focus-within:ring-2 focus-within:ring-app-brand-strong/30 ${
          welcome ? 'min-h-28 gap-2 p-4' : 'gap-1.5 p-2.5'
        }`}
        onSubmit={onSend}
        onDragOver={(event) => {
          if (onAttach && event.dataTransfer.types.includes('Files')) event.preventDefault();
        }}
        onDrop={onDrop}
      >
        {attachments.length > 0 && (
          <AttachmentChips
            attachments={attachments}
            onRemove={onRemoveAttachment}
            onRetry={onRetryAttachment}
          />
        )}
        <label className="sr-only" htmlFor="query">
          Write a message to the assistant
        </label>
        <Textarea
          className={`w-full max-h-48 flex-1 resize-none overflow-y-auto rounded-none border-0 bg-transparent! p-0 text-xs leading-5 text-app-text shadow-none outline-0 placeholder:text-app-text-muted focus-visible:border-0 focus-visible:ring-0 ${
            welcome ? 'min-h-0' : 'min-h-10'
          }`}
          id="query"
          value={value}
          maxLength={10000}
          rows={welcome ? 3 : 2}
          disabled={quotaExhausted}
          placeholder={
            placeholder ??
            (welcome
              ? 'HR should see the employees who joined this year, with name and start date…'
              : 'Describe the app you want to build…')
          }
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        {overBudget > 0 && (
          <p className="text-xs text-app-warning" role="status">
            {overBudget} {overBudget === 1 ? 'file does' : 'files do'} not fit the budget and will
            be searched instead of read in full. Switch other files off in the Files list to read
            more in full.
          </p>
        )}
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {onAttach && (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept={ACCEPT_ATTRIBUTE}
                  multiple
                  hidden
                  onChange={(event) => {
                    attach(Array.from(event.target.files ?? []));
                    event.target.value = '';
                  }}
                />
                <Button
                  variant="outline"
                  type="button"
                  className="h-7 rounded-full border-app-border px-2 text-xs text-app-text-muted"
                  aria-label="Attach files"
                  title={`Attach files (up to ${MAX_ATTACHMENTS_PER_MESSAGE})`}
                  disabled={quotaExhausted || attachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
                  onClick={() => fileInput.current?.click()}
                >
                  <Paperclip className="h-3.5 w-3.5" aria-hidden="true" />
                </Button>
              </>
            )}
            {showSelectionButton && (
              <SelectionButton
                canSelectPreview={previewSelectionToolAvailable}
                selectionMode={selectionMode}
                selectionCount={selectionCount}
                title={selectionTitle}
                onClear={onClearPreviewSelection}
              />
            )}
            {showPlanModeToggle ? (
              <Button
                variant="outline"
                type="button"
                aria-pressed={planMode}
                aria-label={`Plan mode ${planMode ? 'on' : 'off'}`}
                title="Adds a plan approval step before building"
                onClick={onPlanModeChange}
                className={`h-7 rounded-full px-2 text-xs ${
                  planMode
                    ? 'border-app-brand-subtle bg-app-brand-subtle text-app-brand-subtle-foreground'
                    : 'border-app-border text-app-text-muted'
                }`}
              >
                <ListChecks className="h-3.5 w-3.5" aria-hidden="true" />
                Plan
              </Button>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ModelToggle
              model={model}
              disabled={(!busy && loading) || (busy && !canSend) || quotaExhausted}
              onChange={onModelChange}
            />
            {busy && !canSend ? (
              <Button
                className="h-auto rounded-md border-0 bg-app-surface-muted px-3 py-1.5 text-xs font-bold text-app-text hover:bg-app-surface-muted-strong"
                type="button"
                onClick={onStop}
              >
                Stop
              </Button>
            ) : (
              <Button
                aria-label="Send message"
                className="rounded-md border-0 bg-app-action text-app-action-foreground hover:bg-app-action-hover disabled:bg-app-border disabled:text-app-text-faint"
                disabled={!canSend || attachmentsBlocking || (!busy && loading) || quotaExhausted}
                size="icon-sm"
                title={
                  attachmentsBlocking
                    ? 'Waiting for files to finish processing'
                    : selectionCount > 0
                    ? 'Send message (Ctrl+Enter)'
                    : 'Send message'
                }
                type="submit"
              >
                <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">{welcome ? 'Start' : 'Send'}</span>
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
